import React, { useContext, useEffect } from "react";
import { Routes, Route, Outlet, Navigate, useLocation, useNavigate } from "react-router-dom";
import './jsx/index.css';
import "./jsx/chart.css";
import "./jsx/step.css";
import Nav from "./jsx/layouts/nav";
import Footer from "./jsx/layouts/Footer";
import { ThemeContext } from "./context/ThemeContext";

// Dashboard Components
import Home from "./jsx/components/Dashboard/Home";
import DashboardDark from "./jsx/components/Dashboard/DashboardDark";
import ProjectDetails from "./jsx/projects/projectdetails";

// Users, Projects, Tasks, Modules
import UserList from "./jsx/users/userlist";
import AddUser from "./jsx/users/adduser";
import EditUser from "./jsx/users/edituser";
import ProjectList from "./jsx/projects/projectlist";
import AddProject from "./jsx/projects/addproject";
import EditProject from "./jsx/projects/editproject";
import TaskList from "./jsx/tasks/tasklist";
import AddTask from "./jsx/tasks/addtask";
import EditTask from "./jsx/tasks/edittask";
import Modules from "./jsx/modules/modules";
import Subscription from "./jsx/components/AppsMenu/AppProfile/subscription";
import TaskBoard from "./jsx/tasks/taskboard";
import TaskDetails from "./jsx/tasks/taskdetails";
import TaskDailyUpdatesList from "./jsx/tasks/TaskDailyUpdatesList";
import Worksheet from "./jsx/tasks/worksheet";
import EditProfilePage from "./jsx/layouts/nav/EditProfilePage";
import UserSubscription from "./jsx/pages/UserSubscription";
import SubscriptionFeatureRequired from "./jsx/pages/SubscriptionFeatureRequired";

import ChangePassword from "./jsx/users/changePassword";
import AddBranch from "./jsx/settings/addBranch";
import EditBranch from "./jsx/settings/editBranch";
import AddDepartment from "./jsx/settings/addDepartment";
import EditDepartment from "./jsx/settings/editDepartment";
import BranchList from "./jsx/settings/listbranch";
import DepartmentList from "./jsx/settings/listdepartments";
import UserSettings from "./jsx/settings/userSettings";
import { getUserDefaultLandingPage, getResolvedLandingPage } from "./utils/userSettings";
import RouteAccessGuard from "./jsx/components/RouteAccessGuard";
import RenewalPopup from "./jsx/components/RenewalPopup";
import AddSubDepartment from "./jsx/settings/addSubDepartment";
import SubDepartmentList from "./jsx/settings/subDepartmentList";
import EditSubDepartment from "./jsx/settings/editSubDepartment";
import AddDesignation from "./jsx/settings/addDesignation";
import DesignationList from "./jsx/settings/designationList";
import AccessControl from "./jsx/settings/accessControl";
import AddStaff from "./jsx/staff/addStaff";
import StaffList from "./jsx/staff/staffList";
import EditStaff from "./jsx/staff/editStaff";
import StaffDetails from "./jsx/staff/staffDetails";
import StaffAddressForm from "./jsx/staff/StaffAddressForm";
import StaffContactForm from "./jsx/staff/StaffContactForm";
import StaffEmploymentForm from "./jsx/staff/StaffEmploymentForm";
import StaffDocumentForm from "./jsx/staff/StaffDocumentForm";
import StaffDocumentDetails from "./jsx/staff/StaffDocumentDetails";

import CredentialVaultList from './jsx/credentialVault/credentialVaultList';
import PersonalVaultList from './jsx/credentialVault/personalVaultList';
import CredentialVaultForm from './jsx/credentialVault/credentialVaultForm';
import CredentialVaultDetail from './jsx/credentialVault/credentialVaultDetail';
import CredentialVaultGate from './jsx/credentialVault/CredentialVaultGate';
import VaultSettings from './jsx/credentialVault/vaultSettings';

//Vehicle
import AddVehicle from "./jsx/vehicle/addVehicle";
import VehicleList from "./jsx/vehicle/vehicleList";
import EditVehicle from "./jsx/vehicle/editVehicle";
import VehicleDetails from "./jsx/vehicle/vehicleDetails";
import VehicleDocumentForm from "./jsx/vehicle/VehicleDocumentForm";
import VehicleDocumentDetails from "./jsx/vehicle/VehicleDocumentDetails";
import VehicleInsuranceForm from "./jsx/vehicle/VehicleInsuranceForm";
import VehicleRegistrationForm from "./jsx/vehicle/VehicleRegistrationForm";
import DocumentList from "./jsx/Documents/documentList";
import DocumentDetails from "./jsx/Documents/documentDetails";
import AddDocument from "./jsx/Documents/AddDocument";
import EditDocument from "./jsx/Documents/EditDocument";
import AddDocumentCategory from "./jsx/Documents/AddDocumentCatagory";
import DocumentCategoryList from "./jsx/Documents/DocumentCatagoryList";
import DocumentsDashboard from "./jsx/Documents/recentDocuments";
import SharedDocuments from "./jsx/Documents/sharedDocuments";
import FavoriteDocuments from "./jsx/Documents/favoriteDocuments";
import SubscriptionPaymentResult from "./jsx/pages/SubscriptionPaymentResult";
import SubscriptionPaymentSuccess from "./jsx/pages/SubscriptionPaymentSuccess";
import SubscriptionCheckout from "./jsx/pages/SubscriptionCheckout";
import SubscriptionRenewal from "./jsx/pages/SubscriptionRenewal";





const MainLayout = () => {
  const { menuToggle } = useContext(ThemeContext);

  return (
    <div id="main-wrapper" 
    data-header-position="fixed"
    data-sidebar-position="fixed"
    data-layout="vertical"
    className={`show ${menuToggle ? "menu-toggle" : ""}`}
    >
      <Nav />
      <RenewalPopup />
      <div className="content-body">
        <div className="container-fluid">
          <RouteAccessGuard>
            <Outlet />
          </RouteAccessGuard>
        </div>
      </div>
      {/* <Footer /> */}
    </div>
  );
};

// NOTE: Only dashboard/private routes are protected here.
// Public pages like /landing, /login, /register, /about-us, /pricing, /contact-us
// are NOT affected by this file and should be defined in src/jsx/index.js as public routes.

/** Redirects to resolved landing page (checks access; falls back to first accessible if default is removed) */
const LandingPageRedirect = () => {
  const navigate = useNavigate();

  useEffect(() => {
    getResolvedLandingPage().then((path) => {
      navigate(path, { replace: true });
    });
  }, [navigate]);

  return null;
};

const PrivateRoutes = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    const localtoken = localStorage.getItem('accessToken');
    const sessiontoken = sessionStorage.getItem('accessToken');
    const tempAccessToken = localStorage.getItem('tempAccessToken');
    if (!localtoken && !sessiontoken && !tempAccessToken) {
      // Only store current page for redirect if we're not already on login page
      if (location.pathname !== '/login') {
        sessionStorage.setItem('redirectAfterLogin', location.pathname + location.search);
        navigate('/login');
      }
    }
  }, [location, navigate])
  
  return (
    <Routes>
      <Route path="/*" element={<MainLayout />}>
        <Route index element={<LandingPageRedirect />} />
        <Route path="dashboard" element={<Home />} />
        <Route path="dashboard-dark" element={<DashboardDark />} />
        <Route path="task/projectdetails/:projectId" element={<ProjectDetails />} />
        <Route path="task/projectdetails/:projectId/daily-updates" element={<TaskDailyUpdatesList />} />
        
        {/* Task-related routes under /task prefix */}
        <Route path="task/tasklist" element={<TaskList />} />
        <Route path="task/addtask" element={<AddTask />} />
        <Route path="task/edittask" element={<EditTask />} />
        <Route path="task/taskboard" element={<TaskBoard />} />
        <Route path="task/taskdetails/:taskid/daily-updates" element={<TaskDailyUpdatesList />} />
        <Route path="task/taskdetails/:taskid" element={<TaskDetails />} />
        <Route path="task/worksheet" element={<Worksheet />} />
        
        {/* Project-related routes under /task prefix */}
        <Route path="task/projectlist" element={<ProjectList />} />
        <Route path="task/addproject" element={<AddProject />} />
        <Route path="task/editproject" element={<EditProject />} />
        
        {/* Other routes */}
        <Route path="settings/userlist" element={<UserList />} />
        <Route path="settings/adduser" element={<AddUser />} />
        <Route path="settings/edituser" element={<EditUser />} />
        <Route path="settings/userSettings" element={<UserSettings />} />
        <Route path="settings/accessControl" element={<AccessControl />} />
        <Route path="modules" element={<Modules />} />
        <Route path='subscribe' element={<Subscription/>}/>
        <Route path='user-subscription' element={<UserSubscription/>}/>
        <Route path='subscription-feature-required' element={<SubscriptionFeatureRequired/>}/>
        <Route path='edit-profile' element={<EditProfilePage/>}/>
        <Route path="changePassword" element={<ChangePassword />} />
        <Route path="settings/branchList" element={<BranchList />} />
        <Route path="settings/departmentList" element={<DepartmentList />} />
        <Route path="settings/addBranch" element={<AddBranch />} />
        <Route path="settings/editBranch/:id" element={<EditBranch />} />
        <Route path="settings/addDepartment" element={<AddDepartment />} />
        <Route path="settings/editDepartment/:id" element={<EditDepartment />} />
        <Route path="settings/addSubDepartment" element={<AddSubDepartment />} />
        <Route path="settings/subDepartmentList" element={<SubDepartmentList />} />
        <Route path="settings/editSubDepartment/:id" element={<EditSubDepartment />} />
        <Route path="settings/addDesignation" element={<AddDesignation />} />
        <Route path="settings/designationList" element={<DesignationList />} />
        <Route path="staff/staffList" element={<StaffList />} />
        <Route path="staff/addStaff" element={<AddStaff />} />
        <Route path="staff/editStaff/:id" element={<EditStaff />} />
        <Route path="staff/staffDetails/:id" element={<StaffDetails />} />
        <Route path="staff/:staffId/address/add" element={<StaffAddressForm />} />
        <Route path="staff/:staffId/address/:addressId/edit" element={<StaffAddressForm />} />
        <Route path="staff/:staffId/contact/add" element={<StaffContactForm />} />
        <Route path="staff/:staffId/contact/:contactId/edit" element={<StaffContactForm />} />
        <Route path="staff/:staffId/employment/add" element={<StaffEmploymentForm />} />
        <Route path="staff/:staffId/employment/:employmentId/edit" element={<StaffEmploymentForm />} />
        <Route path="staff/:staffId/document/add" element={<StaffDocumentForm />} />
        <Route path="staff/:staffId/document/:documentId/edit" element={<StaffDocumentForm />} />
        <Route path="staff/:staffId/document/:documentId" element={<StaffDocumentDetails />} />

        <Route path="credential-vault" element={<CredentialVaultGate />}>
          <Route path="credentialVaultList" element={<CredentialVaultList />} />
          <Route path="personalVaultList" element={<PersonalVaultList />} />
          <Route path="credentialDetail/:id" element={<CredentialVaultDetail />} />
          <Route path="addCredential" element={<CredentialVaultForm />} />
          <Route path="editCredential/:id" element={<CredentialVaultForm />} />
          <Route path="vaultSettings" element={<VaultSettings />} />
        </Route>
        

        <Route path="vehicle/addVehicle" element={<AddVehicle />} />
        <Route path="vehicle/vehicleList" element={<VehicleList />} />
        <Route path="vehicle/editVehicle/:id" element={<EditVehicle />} />
        <Route path="vehicle/vehicleDetails/:id" element={<VehicleDetails />} />
        <Route path="vehicle/:vehicleId/document/add" element={<VehicleDocumentForm />} />
        <Route path="vehicle/:vehicleId/document/:documentId/edit" element={<VehicleDocumentForm />} />
        <Route path="vehicle/:vehicleId/document/:documentId" element={<VehicleDocumentDetails />} />
        <Route path="vehicle/:vehicleId/insurance/add" element={<VehicleInsuranceForm />} />
        <Route path="vehicle/:vehicleId/insurance/:insuranceId/edit" element={<VehicleInsuranceForm />} />
        <Route path="vehicle/:vehicleId/registration/add" element={<VehicleRegistrationForm />} />
        <Route path="vehicle/:vehicleId/registration/:registrationId/edit" element={<VehicleRegistrationForm />} />

        <Route path="documents/mydocuments" element={<DocumentList />} />
        <Route path="documents/folder/:folderId" element={<DocumentList />} />
        <Route path="documents/details/:documentId" element={<DocumentDetails />} />
        <Route path="documents/edit/:documentId" element={<EditDocument />} />
        <Route path="documents/dashboard" element={<DocumentsDashboard />} />
        <Route path="documents/shared" element={<SharedDocuments />} />
        <Route path="documents/favorites" element={<FavoriteDocuments />} />
        <Route path="documents/upload" element={<AddDocument />} />
        <Route path="documents/addDocumentClassification" element={<AddDocumentCategory />} />
        <Route path="documents/editDocumentClassification/:id" element={<AddDocumentCategory />} />
        <Route path="documents/documentClassifications" element={<DocumentCategoryList />} />

        <Route path="subscription/checkout" element={<SubscriptionCheckout />} />
        <Route path="subscription/renew" element={<SubscriptionRenewal />} />
        <Route path="subscription/payment-result" element={<SubscriptionPaymentResult />} />
        <Route path="subscription/payment-success" element={<SubscriptionPaymentSuccess />} />
      </Route>
    </Routes>
  );
};

export default PrivateRoutes;
